/** A single parametric EQ band */
export interface EqBand {
  /** Center frequency in Hz */
  frequency: number;
  /** Gain in dB (positive = boost, negative = cut) */
  gain: number;
  /** Quality factor */
  q: number;
  /** Filter type */
  type: "PK" | "LSC" | "HSC" | "LP" | "HP" | "NO";
  /** Whether this band is enabled */
  enabled: boolean;
}

/** Complete EQ preset ready for export */
export interface EqPreset {
  /** Preamp gain in dB (typically negative to prevent clipping) */
  preamp: number;
  /** Array of EQ bands */
  bands: EqBand[];
}

/** A frequency/dB data point */
export interface FreqPoint {
  frequency: number;
  db: number;
}

/** 1/N-octave band */
export interface OctaveBand {
  /** Nominal center frequency */
  center: number;
  /** Lower edge frequency */
  low: number;
  /** Upper edge frequency */
  high: number;
  /** Index into this band (for mapping FFT bins) */
  index: number;
}

/** Measurement state */
export type MeasurementState =
  | "idle"
  | "capturing"
  | "averaging"
  | "complete";

/** Audio capture settings */
export interface CaptureSettings {
  fftSize: number;
  sampleRate: number;
  smoothingTimeConstant: number;
}

/** App configuration */
export interface AppConfig {
  averagingTime: number;      // seconds
  smoothingFraction: number;  // denominator: 3, 6, or 12
  eqBandCount: number;        // number of parametric bands to fit
  noiseLevel: number;         // dBFS
  minFreq: number;            // Hz
  maxFreq: number;            // Hz
}

export const DEFAULT_CONFIG: AppConfig = {
  averagingTime: 5,
  smoothingFraction: 6,
  eqBandCount: 10,
  noiseLevel: -20,
  minFreq: 20,
  maxFreq: 20000,
};
